/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Divider,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    LinkOff,
    CheckCircle,
    SyncAlt,
    ArrowBack,
    OpenInNew,
    Bolt,
    AutoAwesome,
    Shield,
    AccountBalance,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    startQuickBooksConnect,
    disconnectQuickBooks,
    fetchQuickBooksStatus,
    syncAll,
    fetchMyBusinesses,
    fetchActiveBusiness,
} from "../../redux/actions/collectAction";
import { setActiveBusiness } from "../../redux/slices/collectSlice";
import {
    selectActiveBusiness,
    selectActiveBusinessId,
    selectActiveBusinessRole,
    selectBusinessMemberships,
    selectIsLoadingBusinesses,
    selectQuickBooks,
    selectIsQuickBooksConnected,
    selectIsStartingQbConnect,
    selectStartQbConnectError,
    selectIsDisconnectingQb,
    selectDisconnectQbError,
    selectIsLoadingQuickBooks,
    selectIsSyncing,
    selectSyncError,
    selectLatestSyncJob,
} from "../../redux/selectors/collectSelector";
import { colors } from "../../utility/types/constant";
import { QuickBooksEnvironment } from "../../utility/types/collectTypes";
import { submitGetCollectSystemInfo } from "../../redux/services/collectService";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectSecondaryButton,
    CollectGhostButton,
    CollectInfoBanner,
} from "./styles";
import { useToast } from "../../providers/ToastProvider";

// ─────────────────────────────────────────────────────────────────────────
// Collect — Connect QuickBooks
// Flow:
//   1) "Connect" button → POST /collect/quickbooks/connect → redirect to authUrl
//   2) Intuit redirects back to backend callback, which redirects back here
//      with ?status=connected (or ?status=error&message=…)
//   3) Once connected, user can run their first sync.
// ─────────────────────────────────────────────────────────────────────────

// The Collect Redux slice is NOT persisted (see store.ts blacklist), and the
// OAuth connect is a full-page redirect (here → Intuit → backend → here). The
// reload wipes the active workspace, which would otherwise default back to the
// user's first membership and hide the Connect button for the workspace they
// were actually connecting. We persist the workspace id here just before
// leaving for Intuit and restore it on return (backend also echoes it back as
// ?businessId=…, which takes precedence).
const QBO_CONNECT_BUSINESS_KEY = "forus_qbo_connect_business_id";

const benefits = [
    {
        icon: <SyncAlt />,
        title: "Real-time AR sync",
        body: "Customers, invoices, and balances stay current, no spreadsheets, no manual updates.",
    },
    {
        icon: <AutoAwesome />,
        title: "Automated reminders",
        body: "Polite, on-brand emails go out before, on, and after the due date automatically.",
    },
    {
        icon: <Bolt />,
        title: "Get paid faster",
        body: "Most teams cut their average days-to-pay by 30–50% within the first few weeks.",
    },
];

const trustItems = [
    {
        icon: <Shield />,
        title: "Read-only by default",
        body: "We only request the QuickBooks scopes we need to read invoices and customers.",
    },
    {
        icon: <AccountBalance />,
        title: "Bank-grade encryption",
        body: "OAuth tokens are encrypted at rest. Disconnect any time with one click.",
    },
];

const CollectConnectQuickBooks: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const activeBusiness = useSelector(selectActiveBusiness);
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const role = useSelector(selectActiveBusinessRole);
    const memberships = useSelector(selectBusinessMemberships);
    const isLoadingBusinesses = useSelector(selectIsLoadingBusinesses);

    const quickbooks = useSelector(selectQuickBooks);
    const isConnected = useSelector(selectIsQuickBooksConnected);
    const isLoadingStatus = useSelector(selectIsLoadingQuickBooks);

    const isStarting = useSelector(selectIsStartingQbConnect);
    const startError = useSelector(selectStartQbConnectError);

    const isDisconnecting = useSelector(selectIsDisconnectingQb);
    const disconnectError = useSelector(selectDisconnectQbError);

    const isSyncing = useSelector(selectIsSyncing);
    const syncError = useSelector(selectSyncError);
    const latestSyncJob = useSelector(selectLatestSyncJob);

    const callbackStatus = searchParams.get("status");
    const callbackMessage = searchParams.get("message");
    const [localBanner, setLocalBanner] = useState<{
        tone: "ok" | "warn" | "info";
        text: string;
    } | null>(null);
    const [quickBooksEnvironment, setQuickBooksEnvironment] =
        useState<QuickBooksEnvironment>("unknown");
    // Track whether we've finished our initial fetch so we don't flash
    // the "Create workspace" CTA before the businesses list has loaded.
    const [hasLoadedBusinesses, setHasLoadedBusinesses] = useState(false);

    useEffect(() => {
        let mounted = true;
        submitGetCollectSystemInfo()
            .then((info) => {
                if (mounted) {
                    setQuickBooksEnvironment(info.quickBooksEnvironment);
                }
            })
            .catch(() => {
                if (mounted) setQuickBooksEnvironment("unknown");
            });
        return () => {
            mounted = false;
        };
    }, []);

    // Restore the workspace we were connecting before the OAuth round-trip.
    // Runs once on mount, BEFORE the bootstrap effect defaults the active
    // workspace to the first membership. Source of truth order:
    //   1) ?businessId=… echoed back by the backend callback (authoritative)
    //   2) localStorage fallback set just before we left for Intuit
    // Without this, an interrupted connect would silently re-pin the user to a
    // different workspace and hide the Connect button for the intended one.
    useEffect(() => {
        const fromUrl = searchParams.get("businessId");
        let fromStorage: string | null = null;
        try {
            fromStorage = window.localStorage.getItem(QBO_CONNECT_BUSINESS_KEY);
        } catch {
            fromStorage = null;
        }
        const desired = fromUrl || fromStorage;
        if (desired) {
            if (desired !== activeBusinessId) {
                dispatch(setActiveBusiness(desired));
                dispatch(fetchActiveBusiness(desired));
            }
            try {
                window.localStorage.removeItem(QBO_CONNECT_BUSINESS_KEY);
            } catch {
                /* ignore storage failures */
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Bootstrap businesses (if user lands here without dashboard first)
    useEffect(() => {
        if (memberships.length === 0 && !isLoadingBusinesses && !hasLoadedBusinesses) {
            dispatch(fetchMyBusinesses()).finally(() => {
                setHasLoadedBusinesses(true);
            });
        } else if (memberships.length > 0 && !hasLoadedBusinesses) {
            setHasLoadedBusinesses(true);
        }
    }, [dispatch, memberships.length, isLoadingBusinesses, hasLoadedBusinesses]);

    // Load QB status when active business is known
    useEffect(() => {
        if (activeBusinessId) {
            // Pull the authoritative business + role for the active tenant so
            // newly created or switched-to workspaces show their name and
            // unlock owner/admin actions (e.g. the Connect button) instead of
            // rendering "Workspace: —" with no controls.
            dispatch(fetchActiveBusiness(undefined));
            dispatch(fetchQuickBooksStatus(undefined));
        }
    }, [dispatch, activeBusinessId]);

    // Handle OAuth callback feedback from URL params
    useEffect(() => {
        if (!callbackStatus) return;
        if (callbackStatus === "connected") {
            setLocalBanner({
                tone: "ok",
                text: "QuickBooks connected successfully. Taking you to your dashboard…",
            });
            // Refresh status to pick up new connection
            if (activeBusinessId) {
                dispatch(fetchQuickBooksStatus(undefined));
            }
            // Auto-redirect to dashboard after a brief beat so the user sees the
            // success banner, then lands on the real workspace view.
            const t = setTimeout(() => {
                navigate("/collect/dashboard", { replace: true });
            }, 1200);

            // // Clean URL after consuming
            // const next = new URLSearchParams(searchParams);
            // next.delete("status");
            // next.delete("message");
            // next.delete("businessId");
            // setSearchParams(next, { replace: true });

            return () => clearTimeout(t);
        }
        if (callbackStatus === "error") {
            setLocalBanner({
                tone: "warn",
                text:
                    callbackMessage ||
                    "We couldn't complete the QuickBooks connection. Please try again.",
            });
        }
        // Clean URL after consuming
        const next = new URLSearchParams(searchParams);
        next.delete("status");
        next.delete("message");
        next.delete("businessId");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callbackStatus, callbackMessage, activeBusinessId, dispatch, navigate, searchParams, setSearchParams]);

    // Fall back to the role on the matching membership when the dedicated
    // activeBusinessRole slot is empty. This keeps owner/admin actions (like
    // the "Connect QuickBooks" button) visible after an OAuth round-trip or a
    // workspace switch that cleared the role without re-fetching it.
    const effectiveRole =
        role ||
        memberships.find((m) => m.businessId === activeBusinessId)?.role ||
        null;
    const canWrite = effectiveRole === "owner" || effectiveRole === "admin";

    const onConnect = async () => {
        const result = await dispatch(startQuickBooksConnect(undefined));
        if (
            startQuickBooksConnect.fulfilled.match(result) &&
            result.payload?.authorizationUrl
        ) {
            if (
                quickBooksEnvironment !== "unknown" &&
                result.payload.environment !== quickBooksEnvironment
            ) {
                toast.error(
                    "QuickBooks environment changed. Refresh this page before connecting.",
                );
                return;
            }
            // Remember which workspace we're connecting so we can re-pin it after
            // the full-page OAuth round-trip (the Collect slice isn't persisted).
            if (activeBusinessId) {
                try {
                    window.localStorage.setItem(
                        QBO_CONNECT_BUSINESS_KEY,
                        activeBusinessId,
                    );
                } catch {
                    /* ignore storage failures — backend also echoes businessId */
                }
            }
            window.location.href = result.payload.authorizationUrl;
        }
    };

    const onDisconnect = async () => {
        if (
            !window.confirm(
                "Disconnect QuickBooks? Your reminders will stop running until you reconnect.",
            )
        ) {
            return;
        }
        await dispatch(disconnectQuickBooks(undefined));
        dispatch(fetchQuickBooksStatus(undefined));
    };

    const onSync = async () => {
        const result: any = await dispatch(syncAll(undefined));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("QuickBooks data synced.");
        } else {
            toast.error("Sync failed. Try reconnecting if the problem persists.");
        }
    };

    // ── Loading shell while we discover the business ─────────────
    // Show a spinner until we've completed at least one businesses fetch.
    // This prevents flashing the "Create workspace" CTA on OAuth return
    // or whenever the page mounts before the list has resolved.
    if (!activeBusinessId && (!hasLoadedBusinesses || isLoadingBusinesses)) {
        return (
            <CollectPageWrapper>
                <CollectHeroBanner>
                    <CollectHeroInner>
                        <CollectBadge label="AARRP · Collect" />
                        <CollectHeroTitle>Loading your workspace…</CollectHeroTitle>
                    </CollectHeroInner>
                </CollectHeroBanner>
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress sx={{ color: colors.primary }} />
                </Box>
            </CollectPageWrapper>
        );
    }

    // ── No business yet (and we've finished loading) ──────────────────
    if (!activeBusinessId) {
        return (
            <CollectPageWrapper>
                <CollectHeroBanner>
                    <CollectHeroInner>
                        <CollectBadge label="AARRP · Collect" />
                        <CollectHeroTitle>Create a workspace first</CollectHeroTitle>
                        <CollectHeroSubtitle>
                            Before you can connect QuickBooks, set up your Collect workspace.
                        </CollectHeroSubtitle>
                    </CollectHeroInner>
                </CollectHeroBanner>
                <Box
                    sx={{
                        maxWidth: 640,
                        margin: "0 auto",
                        padding: { xs: "24px 16px 48px", sm: "32px 24px 64px" },
                    }}
                >
                    <CollectPrimaryButton onClick={() => navigate("/collect/setup")}>
                        Set up your workspace
                    </CollectPrimaryButton>
                </Box>
            </CollectPageWrapper>
        );
    }

    return (
        <CollectPageWrapper>
            {/* ─── Hero ───────────────────────────────────────────────── */}
            <CollectHeroBanner
                sx={{
                    padding: {
                        xs: "28px 16px 32px",
                        sm: "40px 24px 44px",
                        md: "48px 24px 56px",
                    },
                }}
            >
                <CollectHeroInner sx={{ textAlign: "center" }}>
                    <CollectBadge
                        label="QuickBooks Integration"
                        sx={{ margin: "0 auto 12px" }}
                    />
                    <CollectHeroTitle
                        sx={{
                            fontSize: { xs: "1.6rem", sm: "2rem", md: "2.4rem" },
                            marginBottom: "8px",
                        }}
                    >
                        Connect QuickBooks Online
                    </CollectHeroTitle>
                    <CollectHeroSubtitle
                        sx={{
                            maxWidth: 620,
                            margin: "0 auto",
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                        }}
                    >
                        Securely link your QuickBooks Online company so Collect can read your
                        customers, invoices, and balances and start automating reminders for
                        you.
                    </CollectHeroSubtitle>
                </CollectHeroInner>
            </CollectHeroBanner>

            {/* ─── Body ───────────────────────────────────────────────── */}
            <Box
                sx={{
                    maxWidth: 960,
                    margin: "0 auto",
                    padding: {
                        xs: "20px 14px 48px",
                        sm: "32px 24px 56px",
                        md: "40px 24px 72px",
                    },
                }}
            >
                {/* Banner feedback (callback, errors) */}
                <CollectInfoBanner
                    tone={
                        quickBooksEnvironment === "production"
                            ? "info"
                            : "warn"
                    }
                    sx={{ marginBottom: { xs: "16px", sm: "20px" } }}
                >
                    <Box flex={1}>
                        {quickBooksEnvironment === "sandbox" ? (
                            <><strong>QuickBooks Sandbox</strong> · Only sandbox companies can be connected.</>
                        ) : quickBooksEnvironment === "production" ? (
                            <><strong>QuickBooks Production</strong> · Only live companies can be connected.</>
                        ) : (
                            <><strong>QuickBooks environment unavailable.</strong> Connection is disabled.</>
                        )}
                    </Box>
                </CollectInfoBanner>
                {localBanner && (
                    <CollectInfoBanner
                        tone={localBanner.tone}
                        sx={{ marginBottom: { xs: "16px", sm: "20px" } }}
                    >
                        {localBanner.tone === "ok" ? (
                            <CheckCircle fontSize="small" />
                        ) : null}
                        <Box flex={1}>{localBanner.text}</Box>
                    </CollectInfoBanner>
                )}
                {startError && (
                    <CollectInfoBanner tone="warn" sx={{ marginBottom: "20px" }}>
                        <Box flex={1}>{startError}</Box>
                    </CollectInfoBanner>
                )}
                {disconnectError && (
                    <CollectInfoBanner tone="warn" sx={{ marginBottom: "20px" }}>
                        <Box flex={1}>{disconnectError}</Box>
                    </CollectInfoBanner>
                )}
                {syncError && (
                    <CollectInfoBanner tone="warn" sx={{ marginBottom: "20px" }}>
                        <Box flex={1}>{syncError}</Box>
                    </CollectInfoBanner>
                )}

                {/* ─── Status card ───────────────────────────────────── */}
                <CollectSectionCard
                    sx={{
                        padding: { xs: "20px 16px", sm: "28px" },
                        marginBottom: { xs: "20px", sm: "28px" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            gap: { xs: 2, sm: 3 },
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={2} flex={1}>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "14px",
                                    backgroundColor: isConnected
                                        ? "rgba(30,193,149,0.12)"
                                        : "rgba(20, 58, 162, 0.08)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: isConnected
                                        ? colors.mainSecondary
                                        : colors.blueRibbon,
                                    flexShrink: 0,
                                }}
                            >
                                {isConnected ? (
                                    <CheckCircle sx={{ fontSize: 32 }} />
                                ) : (
                                    <AccountBalance sx={{ fontSize: 30 }} />
                                )}
                            </Box>
                            <Box flex={1} minWidth={0}>
                                <CollectSectionTitle
                                    sx={{ marginBottom: "4px", fontSize: "1.05rem" }}
                                >
                                    {isLoadingStatus
                                        ? "Checking status…"
                                        : isConnected
                                            ? "QuickBooks connected"
                                            : "Not connected yet"}
                                </CollectSectionTitle>
                                <CollectSectionSubtitle sx={{ fontSize: "0.85rem" }}>
                                    {isConnected
                                        ? quickbooks?.companyName
                                            ? `Linked ${quickbooks.environment || quickBooksEnvironment} company: ${quickbooks.companyName}`
                                            : `Workspace: ${activeBusiness?.name || "—"}`
                                        : `Workspace: ${activeBusiness?.name || "—"}`}
                                </CollectSectionSubtitle>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                gap: 1.5,
                                flexShrink: 0,
                                width: { xs: "100%", sm: "auto" },
                                flexDirection: { xs: "column-reverse", sm: "row" },
                            }}
                        >
                            {isConnected ? (
                                <>
                                    {canWrite && (
                                        <CollectSecondaryButton
                                            startIcon={
                                                isDisconnecting ? (
                                                    <CircularProgress
                                                        size={14}
                                                        sx={{ color: colors.primary }}
                                                    />
                                                ) : (
                                                    <LinkOff />
                                                )
                                            }
                                            onClick={onDisconnect}
                                            disabled={isDisconnecting}
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            {isDisconnecting ? "Disconnecting…" : "Disconnect"}
                                        </CollectSecondaryButton>
                                    )}
                                    {canWrite && (
                                        <CollectPrimaryButton
                                            startIcon={
                                                isSyncing ? (
                                                    <CircularProgress size={14} sx={{ color: "#fff" }} />
                                                ) : (
                                                    <SyncAlt />
                                                )
                                            }
                                            onClick={onSync}
                                            disabled={isSyncing}
                                            sx={{
                                                width: { xs: "100%", sm: "auto" },
                                                minWidth: { sm: 170 },
                                            }}
                                        >
                                            {isSyncing ? "Syncing…" : "Sync now"}
                                        </CollectPrimaryButton>
                                    )}
                                </>
                            ) : (
                                canWrite && (
                                    <CollectPrimaryButton
                                        startIcon={
                                            isStarting ? (
                                                <CircularProgress size={14} sx={{ color: "#fff" }} />
                                            ) : (
                                                <OpenInNew />
                                            )
                                        }
                                        onClick={onConnect}
                                        disabled={
                                            isStarting ||
                                            quickBooksEnvironment === "unknown"
                                        }
                                        sx={{
                                            width: { xs: "100%", sm: "auto" },
                                            minWidth: { sm: 210 },
                                        }}
                                    >
                                        {isStarting
                                            ? "Redirecting…"
                                            : quickBooksEnvironment === "sandbox"
                                                ? "Connect Sandbox QuickBooks"
                                                : "Connect Production QuickBooks"}
                                    </CollectPrimaryButton>
                                )
                            )}
                        </Box>
                    </Box>

                    {!canWrite && (
                        <Box
                            sx={{
                                marginTop: 2,
                                fontSize: "0.8rem",
                                color: "#6b7280",
                                fontFamily: "'Montserrat', sans-serif",
                            }}
                        >
                            Only owners and admins can manage the QuickBooks connection.
                        </Box>
                    )}

                    {isConnected && latestSyncJob && (
                        <>
                            <Divider sx={{ my: 2.5 }} />
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "0.8rem",
                                    color: "#6b7280",
                                }}
                            >
                                Last sync: {latestSyncJob.type} ·{" "}
                                <strong style={{ textTransform: "capitalize" }}>
                                    {latestSyncJob.status}
                                </strong>
                                {typeof latestSyncJob.itemsProcessed === "number"
                                    ? ` · ${latestSyncJob.itemsProcessed} records`
                                    : ""}
                            </Typography>
                        </>
                    )}
                </CollectSectionCard>

                {/* ─── Benefits ───────────────────────────────────────── */}
                <CollectSectionCard
                    sx={{
                        padding: { xs: "20px 16px", sm: "28px" },
                        marginBottom: { xs: "20px", sm: "28px" },
                    }}
                >
                    <Box sx={{ marginBottom: 2 }}>
                        <CollectSectionTitle>What you'll unlock</CollectSectionTitle>
                        <CollectSectionSubtitle>
                            The moment QuickBooks is connected, Collect goes to work.
                        </CollectSectionSubtitle>
                    </Box>
                    <Grid container spacing={isMobile ? 2 : 3}>
                        {benefits.map((b) => (
                            <Grid item xs={12} sm={4} key={b.title}>
                                <Box
                                    sx={{
                                        padding: { xs: "16px", sm: "18px" },
                                        borderRadius: "12px",
                                        border: "1px solid rgba(15,23,42,0.06)",
                                        backgroundColor: "rgba(248, 250, 252, 0.7)",
                                        height: "100%",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "10px",
                                            backgroundColor: "rgba(30,193,149,0.12)",
                                            color: colors.mainSecondary,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginBottom: 1.5,
                                        }}
                                    >
                                        {b.icon}
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            fontSize: "0.95rem",
                                            color: colors.primary,
                                            marginBottom: "4px",
                                        }}
                                    >
                                        {b.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: "0.83rem",
                                            color: "#6b7280",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {b.body}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CollectSectionCard>

                {/* ─── Trust ──────────────────────────────────────────── */}
                <CollectSectionCard
                    sx={{
                        padding: { xs: "20px 16px", sm: "28px" },
                        marginBottom: { xs: "20px", sm: "28px" },
                    }}
                >
                    <Box sx={{ marginBottom: 2 }}>
                        <CollectSectionTitle>Privacy & security</CollectSectionTitle>
                        <CollectSectionSubtitle>
                            You stay in control of your QuickBooks data.
                        </CollectSectionSubtitle>
                    </Box>
                    <Grid container spacing={isMobile ? 2 : 3}>
                        {trustItems.map((t) => (
                            <Grid item xs={12} sm={6} key={t.title}>
                                <Box display="flex" gap={1.5} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "10px",
                                            backgroundColor: "rgba(20,58,162,0.08)",
                                            color: colors.blueRibbon,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {t.icon}
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "0.9rem",
                                                color: colors.primary,
                                                marginBottom: "2px",
                                            }}
                                        >
                                            {t.title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Montserrat', sans-serif",
                                                fontSize: "0.82rem",
                                                color: "#6b7280",
                                                lineHeight: 1.55,
                                            }}
                                        >
                                            {t.body}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CollectSectionCard>

                {/* ─── Footer nav ─────────────────────────────────────── */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column-reverse", sm: "row" },
                        gap: 1.5,
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                    }}
                >
                    <CollectGhostButton
                        startIcon={<ArrowBack />}
                        onClick={() => navigate("/collect/dashboard")}
                    >
                        Back to dashboard
                    </CollectGhostButton>
                    {isConnected && (
                        <CollectSecondaryButton
                            onClick={() => navigate("/collect/invoices")}
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                            View invoices
                        </CollectSecondaryButton>
                    )}
                </Box>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectConnectQuickBooks;
